/**
 * Skin Salt — External Add-to-Cart + Checkout Redirect
 * =====================================================
 * Drop this script onto vsl.skinsalt.co (or any external page) to let
 * customers pick a subscription variant + flavors, then land directly
 * on Shopify checkout with everything pre-loaded.
 *
 * CROSS-DOMAIN STRATEGY:
 * Since vsl.skinsalt.co is a different domain from your Shopify store,
 * we can't use /cart/add.js (cookies aren't shared cross-origin).
 * Instead we build a Shopify cart permalink URL that:
 *   1. Clears the cart  (/cart/clear → redirect)
 *   2. Adds the variant with selling plan + flavor properties
 *   3. Redirects straight to /checkout
 *
 * Cart permalink format:
 *   https://store.myshopify.com/cart/VARIANT_ID:QTY?selling_plan=PLAN_ID
 *     &properties[Flavor1]=2&properties[Flavor2]=1&checkout=true
 *
 * SETUP:
 *   1. Fill in VARIANT_CONFIG with your real Shopify variant IDs
 *      and selling plan IDs.
 *   2. Set SHOPIFY_DOMAIN to your Shopify domain.
 *   3. Include this script: <script src="skinsalt-external-cart.js"></script>
 *   4. Call SkinSaltCart.addAndCheckout() from your page UI.
 *
 * HOW TO GET IDs:
 *   Open your storefront (not admin) and run in the browser console:
 *     fetch('/products/skinsalt.js').then(r=>r.json()).then(function(p){
 *       p.variants.forEach(function(v){
 *         console.log('Variant:', v.id, v.title, '| Price:', v.price);
 *         if (v.selling_plan_allocations) {
 *           v.selling_plan_allocations.forEach(function(sp){
 *             console.log('  Selling Plan:', sp.selling_plan.id, sp.selling_plan.name);
 *           });
 *         }
 *       });
 *     });
 */

(function () {
  'use strict';

  // ── Configuration ──────────────────────────────────────────────────

  var SHOPIFY_DOMAIN = 'https://skin-salt-staging.myshopify.com';
  // Production: var SHOPIFY_DOMAIN = 'https://skinsalt.co';

  /**
   * Map each package tier to its Shopify variant ID and selling plan ID.
   * packCount = total flavor packs the customer must select.
   *
   * IMPORTANT: Replace the null values with your real IDs!
   */
  var VARIANT_CONFIG = {
    '3plus1': {
      variantId: null,       // TODO: e.g. 48839212679479
      sellingPlanId: null,   // TODO: e.g. 1773469991
      packCount: 4,
      label: '3 + 1 Free!'
    },
    '2box': {
      variantId: null,       // TODO: e.g. 48839212712247
      sellingPlanId: null,   // TODO: e.g. 1773470023
      packCount: 2,
      label: '2 Boxes'
    },
    '1box': {
      variantId: null,       // TODO: e.g. 48839212745015
      sellingPlanId: null,   // TODO: e.g. 1773470055
      packCount: 1,
      label: '1 Box'
    }
  };

  var AVAILABLE_FLAVORS = [
    'Summer Peach',
    'Passion Fruit Sunrise',
    'Triple Berry Burst',
    'Lemon Lime Spritz'
  ];

  /** Flavor name → product data mapping. */
  var FLAVOR_DATA = {
    'Lemon Lime Spritz':    { sku: 'SKLLS', product_id: '10158620934427' },
    'Passion Fruit Sunrise': { sku: 'SKPFS', product_id: '10158619328795' },
    'Summer Peach':          { sku: 'SKPCH', product_id: '10158620573979' },
    'Triple Berry Burst':    { sku: 'SKTBB', product_id: '10158620737819' }
  };

  // ── Validation ─────────────────────────────────────────────────────

  /**
   * Validate that flavor selections add up to the required pack count.
   * @param {Object} flavorSelections  e.g. { "Summer Peach": 2, "Triple Berry Burst": 2 }
   * @param {number} requiredCount     e.g. 4
   * @returns {{ valid: boolean, total: number, required: number }}
   */
  function validateFlavors(flavorSelections, requiredCount) {
    var total = 0;
    for (var flavor in flavorSelections) {
      if (flavorSelections.hasOwnProperty(flavor)) {
        var count = parseInt(flavorSelections[flavor], 10) || 0;
        if (count > 0) total += count;
      }
    }
    return {
      valid: total === requiredCount,
      total: total,
      required: requiredCount
    };
  }

  // ── Cart Permalink Builder ─────────────────────────────────────────

  /**
   * Build a Shopify cart permalink URL that adds the variant with
   * selling plan and flavor properties, then redirects to checkout.
   *
   * Format:
   *   /cart/{variant_id}:1?selling_plan={plan_id}
   *     &properties[Summer Peach]=2&properties[Passion Fruit Sunrise]=1
   *     &checkout=true
   */
  function buildCheckoutUrl(variantId, sellingPlanId, flavorSelections) {
    var url = SHOPIFY_DOMAIN + '/cart/' + variantId + ':1';

    var params = [];

    // Selling plan
    if (sellingPlanId) {
      params.push('selling_plan=' + encodeURIComponent(sellingPlanId));
    }

    // Visible flavor properties + hidden _line_items for Stord
    var lineItems = [];
    for (var flavor in flavorSelections) {
      if (flavorSelections.hasOwnProperty(flavor)) {
        var count = parseInt(flavorSelections[flavor], 10) || 0;
        if (count > 0) {
          /* Visible property for cart & checkout display */
          params.push(
            'properties[' + encodeURIComponent(flavor) + ']=' +
            encodeURIComponent(count)
          );
          var data = FLAVOR_DATA[flavor] || {};
          lineItems.push({
            product_id: data.product_id || '',
            title: flavor,
            sku: data.sku || '',
            quantity: count
          });
        }
      }
    }
    /* Hidden JSON payload for Stord fulfillment */
    if (lineItems.length > 0) {
      params.push(
        'properties[' + encodeURIComponent('_line_items') + ']=' +
        encodeURIComponent(JSON.stringify(lineItems))
      );
    }

    // Go straight to checkout
    params.push('checkout=true');

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    return url;
  }

  // ── Main Public API ────────────────────────────────────────────────

  /**
   * Validate flavors, build cart permalink, and redirect to Shopify checkout.
   *
   * @param {string} packageTier       One of: '3plus1', '2box', '1box'
   * @param {Object} flavorSelections  e.g. { "Summer Peach": 2, "Passion Fruit Sunrise": 1, "Triple Berry Burst": 1 }
   * @param {Object} [options]
   * @param {Function} [options.onError]       Called with error message string
   * @param {Function} [options.onValidation]  Called with { valid, total, required }
   * @param {Function} [options.onRedirecting] Called right before redirect (for loading UI)
   * @param {boolean}  [options.returnUrl]     If true, returns the URL instead of redirecting
   *
   * @returns {string|undefined}  The checkout URL if options.returnUrl is true
   *
   * @example
   *   // Simplest usage — redirects immediately
   *   SkinSaltCart.addAndCheckout('3plus1', {
   *     'Summer Peach': 2,
   *     'Passion Fruit Sunrise': 1,
   *     'Triple Berry Burst': 1
   *   });
   *
   * @example
   *   // With error handling and loading state
   *   SkinSaltCart.addAndCheckout('2box', {
   *     'Summer Peach': 1,
   *     'Lemon Lime Spritz': 1
   *   }, {
   *     onError: function(msg) { alert(msg); },
   *     onRedirecting: function() { btn.textContent = 'Redirecting...'; }
   *   });
   *
   * @example
   *   // Get the URL without redirecting
   *   var url = SkinSaltCart.addAndCheckout('1box', {
   *     'Triple Berry Burst': 1
   *   }, { returnUrl: true });
   *   console.log(url);
   */
  function addAndCheckout(packageTier, flavorSelections, options) {
    options = options || {};

    // Look up config
    var config = VARIANT_CONFIG[packageTier];
    if (!config) {
      var msg = 'Invalid package tier: "' + packageTier + '". Use: 3plus1, 2box, or 1box.';
      if (options.onError) options.onError(msg);
      else console.error('[SkinSaltCart]', msg);
      return;
    }

    if (!config.variantId) {
      var msg2 = 'Variant ID not configured for "' + packageTier + '". Update VARIANT_CONFIG in skinsalt-external-cart.js.';
      if (options.onError) options.onError(msg2);
      else console.error('[SkinSaltCart]', msg2);
      return;
    }

    // Validate flavor count
    var validation = validateFlavors(flavorSelections, config.packCount);
    if (options.onValidation) options.onValidation(validation);

    if (!validation.valid) {
      var msg3 = 'Please select exactly ' + config.packCount + ' flavor pack' +
                 (config.packCount > 1 ? 's' : '') +
                 '. You selected ' + validation.total + '.';
      if (options.onError) options.onError(msg3);
      else console.error('[SkinSaltCart]', msg3);
      return;
    }

    // Build the checkout URL
    var checkoutUrl = buildCheckoutUrl(
      config.variantId,
      config.sellingPlanId,
      flavorSelections
    );

    // Return URL only
    if (options.returnUrl) {
      return checkoutUrl;
    }

    // Redirect
    if (options.onRedirecting) options.onRedirecting();
    window.location.href = checkoutUrl;
  }

  /**
   * Build a checkout URL without redirecting. Useful for setting <a> hrefs.
   * Same params as addAndCheckout but always returns the URL string.
   */
  function getCheckoutUrl(packageTier, flavorSelections) {
    var config = VARIANT_CONFIG[packageTier];
    if (!config || !config.variantId) return null;
    return buildCheckoutUrl(config.variantId, config.sellingPlanId, flavorSelections);
  }

  // ── Expose public API ──────────────────────────────────────────────

  window.SkinSaltCart = {
    addAndCheckout: addAndCheckout,
    getCheckoutUrl: getCheckoutUrl,
    buildCheckoutUrl: buildCheckoutUrl,
    validateFlavors: validateFlavors,
    VARIANT_CONFIG: VARIANT_CONFIG,
    AVAILABLE_FLAVORS: AVAILABLE_FLAVORS,
    SHOPIFY_DOMAIN: SHOPIFY_DOMAIN
  };

})();
